import { Navigate, Route, Routes } from 'react-router';

import { AppShell } from '@/components/layout/app-shell';
import { RequireAdmin } from '@/components/auth/require-admin';
import { RequireStaff } from '@/components/auth/require-staff';
import { useAppRoutes } from '@/hooks/use-app-routes';
import { HomePage } from '@/pages/home';
import { ReportWizard } from '@/pages/report/report-wizard';
import { TrackingPage } from '@/pages/track/tracking-page';
import { MyReportsPage } from '@/pages/my-reports/my-reports-page';
import { MapExplorerPage } from '@/pages/map/map-explorer';
import { DigitalTwinPage } from '@/pages/twin/digital-twin';
import { ConferenceTwinPage } from '@/pages/twin/conference-twin';
import { FallsCreekTwinPage } from '@/pages/twin/falls-creek-twin';
import { WoodsmokeTwinPage } from '@/pages/twin/woodsmoke-twin';
import { TwinHubPage } from '@/pages/twin/twin-hub';
import { ArOverlayPage } from '@/pages/ar/ar-overlay';
import { AssetRegisterPage } from '@/pages/workspace/asset-register';
import { ImportWizardPage } from '@/pages/workspace/import-wizard';
import { BoundaryEditorPage } from '@/pages/workspace/boundary-editor';
import { HuntPage } from '@/pages/hunt/hunt-page';
import { CheckinPage } from '@/pages/hunt/checkin-page';
import { GpsCapturePage } from '@/pages/hunt/gps-capture-page';
import { LeaderboardLivePage } from '@/pages/hunt/leaderboard-live';
import { HuntPosterPage } from '@/pages/hunt/hunt-poster';
import { PurposeRoutePage } from '@/pages/purposes/purpose-page';
import { AdminLayout } from '@/pages/admin/admin-layout';
import { AdminAuditLogPage } from '@/pages/admin/audit-log';
import { AdminUsersPage } from '@/pages/admin/users';

export function App() {
  const routes = useAppRoutes();

  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/" element={<HomePage />} />
      <Route path="/report" element={<ReportWizard />} />
      <Route path="/track/:token" element={<TrackingPage />} />
      <Route path="/my-reports" element={<MyReportsPage />} />
      <Route path="/map" element={<MapExplorerPage />} />
      <Route path="/twin" element={<TwinHubPage />} />
      <Route path="/events/falls-creek/twin" element={<FallsCreekTwinPage />} />
      <Route path="/events/alpine-woodsmoke-unit-4/twin" element={<WoodsmokeTwinPage />} />
      <Route path="/events/:eventSlug/twin" element={<ConferenceTwinPage />} />
      <Route path="/twin/locations/:locationSlug" element={<DigitalTwinPage />} />
      <Route path="/twin/:regionId" element={<DigitalTwinPage />} />
      <Route path="/ar" element={<ArOverlayPage />} />
      <Route path="/hunt/:huntSlug" element={<HuntPage />} />
      <Route path="/hunt/:huntSlug/checkin/:stationCode" element={<CheckinPage />} />
      <Route path="/hunt/:huntSlug/capture/:stationId" element={<GpsCapturePage />} />
      <Route path="/hunt/:huntSlug/leaderboard/live" element={<LeaderboardLivePage />} />
      <Route path="/for/:purpose" element={<PurposeRoutePage />} />

      {/* Staff workspace — requires staff role (effective) */}
      <Route element={<RequireStaff />}>
        <Route path="/workspace/hunts/:huntId/posters" element={<HuntPosterPage />} />
        <Route path="/workspace/hunts/:huntId/posters/:stationId" element={<HuntPosterPage />} />
        <Route path="/workspace/assets" element={<AssetRegisterPage />} />
        <Route path="/workspace/import" element={<ImportWizardPage />} />
        <Route path="/workspace/boundaries" element={<BoundaryEditorPage />} />
      </Route>

      {/* Auth-gated dashboard routes */}
      <Route element={<AppShell />}>
        {routes.map((route) => (
          <Route key={route.id} path={route.path} element={route.element} />
        ))}
        <Route element={<RequireAdmin />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/workspace" replace />} />
      </Route>
    </Routes>
  );
}
